import React, { useState, useEffect, useRef } from "react";
import apiClient from "../../../../api/apiClient";
import Loading from "../../../../components/Loading/Loading";
import CompanyPipelineCard from "./shared/CompanyPipelineCard";

const sortContacts = (leads) =>
    [...leads].sort((a, b) => {
        const score = (l) => (l.lastRepliedAt ? 2 : l.lastOpenedAt ? 1 : 0);
        const diff = score(b) - score(a);
        if (diff !== 0) return diff;
        return new Date(a.createdAt) - new Date(b.createdAt);
    });

/**
 * Groups leads into company buckets.
 * Resolution order per lead:
 *   1. lead.companyId → direct FK match in companiesById
 *   2. lead.company text → case-insensitive name match in companiesByName
 *   3. no match → group by raw text with null company metadata
 */
const groupByCompany = (leads, companiesById, companiesByName) => {
    const map = new Map();
    
    for (const lead of leads) {
        const cid = lead.companyId ?? null;
        let co = null;

        if (cid != null) {
            co = companiesById[cid] || null;
        }
        if (!co && lead.company?.trim()) {
            co = companiesByName[lead.company.trim().toLowerCase()] || null;
        }

        // Key: prefer stable DB id so moves work correctly
        const key = co ? `cid:${co.id}` : (lead.company?.trim() || "__no_company__");

        if (!map.has(key)) {
            map.set(key, {
                displayName:  co?.businessName || lead.company?.trim() || "No Company",
                companyId:    co?.id ?? null,
                businessType: co?.businessType  || null,
                industryType: co?.industryType  || null,
                contacts:     [],
            });
        }
        map.get(key).contacts.push(lead);
    }

    for (const [, group] of map.entries()) {
        group.contacts = sortContacts(group.contacts);
    }

    return map;
};

const PipelineBoard = ({ stages, onSelectLead, onSelectCompany }) => {
    const [columns, setColumns]   = useState([]);
    const [loading, setLoading]   = useState(true);
    const draggingGroup           = useRef(null);

    useEffect(() => {
        if (!stages.length) return;
        fetchData();
    }, [stages]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchData = async () => {
        setLoading(true);
        try {
            const [leadsRes, companiesRes] = await Promise.all([
                apiClient.get("/admin/crm/leads?limit=100&sort=createdAt&order=asc&includeEngagement=true"),
                apiClient.get("/admin/crm/companies"),
            ]);

            const leads     = leadsRes.data.leads       || [];
            const companies = companiesRes.data.companies || [];

            const byId   = {};
            const byName = {};
            for (const c of companies) {
                byId[c.id] = c;
                if (c.businessName) byName[c.businessName.toLowerCase().trim()] = c;
            }

            buildColumns(leads, byId, byName);
        } catch (err) {
            console.error("[PipelineBoard] fetch error:", err);
        } finally {
            setLoading(false);
        }
    };

    const buildColumns = (leads, byId, byName) => {
        const cols = stages.map((s) => {
            const stageLeads = leads.filter((l) => l.stageId === s.id);
            const companies  = groupByCompany(stageLeads, byId, byName);
            return { ...s, companies };
        });
        setColumns(cols);
    };

    /* ── Drag ──────────────────────────────────────────────────────── */

    const handleDragStart = (groupKey, stageId, leadIds, e) => {
        draggingGroup.current = { groupKey, stageId, leadIds };
        e.dataTransfer.effectAllowed = "move";
    };

    const handleDragEnd = () => { draggingGroup.current = null; };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    };

    const handleDrop = async (targetStageId, e) => {
        e.preventDefault();
        const drag = draggingGroup.current;
        if (!drag || targetStageId === drag.stageId) return;
        const { groupKey, stageId: fromStageId, leadIds } = drag;

        setColumns((prev) =>
            prev.map((col) => {
                if (col.id === fromStageId) {
                    const next = new Map(col.companies);
                    next.delete(groupKey);
                    return { ...col, companies: next };
                }
                if (col.id === targetStageId) {
                    const srcCol     = prev.find((c) => c.id === fromStageId);
                    const movedGroup = srcCol?.companies.get(groupKey);
                    if (!movedGroup) return col;
                    const updated = movedGroup.contacts.map((l) => ({ ...l, stageId: targetStageId }));
                    const next    = new Map(col.companies);
                    const existing = next.get(groupKey);
                    next.set(groupKey, {
                        ...movedGroup,
                        contacts: sortContacts([...(existing?.contacts || []), ...updated]),
                    });
                    return { ...col, companies: next };
                }
                return col;
            })
        );

        try {
            await Promise.all(leadIds.map((id) =>
                apiClient.put(`/admin/crm/leads/${id}/stage`, { stageId: targetStageId })
            ));
        } catch (err) {
            console.error("[PipelineBoard] stage update failed:", err);
            fetchData();
        }
    };

    /* ── Helpers ───────────────────────────────────────────────────── */

    const colTotal = (companies) => {
        let sum = 0;
        for (const g of companies.values())
            for (const l of g.contacts) sum += parseFloat(l.estimatedValue) || 0;
        return sum;
    };

    const fmtVal = (v) =>
        v > 0 ? `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : null;

    if (loading) return <div className="admin-loading-wrapper"><Loading /></div>;

    return (
        <div className="crm-pipeline-board">
            {columns.map((col) => {
                const totalVal   = colTotal(col.companies);
                const totalCards = col.companies.size;

                return (
                    <div key={col.id} className="crm-kanban-column">
                        <div
                            className="crm-kanban-column-header"
                            style={{ backgroundColor: col.color }}
                        >
                            <span>{col.name}</span>
                            <span className="crm-kanban-column-meta">
                                {totalCards} {totalCards === 1 ? "co." : "cos."}
                                {totalVal > 0 ? ` · ${fmtVal(totalVal)}` : ""}
                            </span>
                        </div>

                        <div
                            className="crm-kanban-cards"
                            onDrop={(e) => handleDrop(col.id, e)}
                            onDragOver={handleDragOver}
                        >
                            {totalCards === 0 && (
                                <p style={{ color: "#9ca3af", fontSize: 12, textAlign: "center", padding: "16px 0" }}>
                                    No companies
                                </p>
                            )}

                            {[...col.companies.entries()].map(([groupKey, group]) => {
                                const leadIds = group.contacts.map((l) => l.id);
                                return (
                                    <CompanyPipelineCard
                                        key={groupKey}
                                        companyName={group.displayName}
                                        companyId={group.companyId}
                                        businessType={group.businessType}
                                        industryType={group.industryType}
                                        stageColor={col.color}
                                        contacts={group.contacts}
                                        onSelectLead={onSelectLead}
                                        onSelectCompany={onSelectCompany}
                                        onDragStart={(e) => handleDragStart(groupKey, col.id, leadIds, e)}
                                        onDragEnd={handleDragEnd}
                                    />
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default PipelineBoard;
